import React, { useState, useEffect, useMemo } from 'react';
import { Slider } from '@/components/ui/slider';
import NumericInput from '@/components/NumericInput';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Pie, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, Filler } from 'chart.js';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Download, Share2, User, AlertTriangle, Info, TrendingUp, Phone } from 'lucide-react';
import { formatPrice, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import logoMain from '@/assets/logo-main.png';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, Filler);

const MortgageCalculator = () => {
    // Inputs - Inicializados con valores por defecto para mostrar cálculo inmediato
    const [price, setPrice] = useState(150000);
    const [downPaymentPercent, setDownPaymentPercent] = useState(20);
    const [term, setTerm] = useState(25);
    const [interestRate, setInterestRate] = useState(7.5);
    const [income, setIncome] = useState('');
    const [showAllAmortization, setShowAllAmortization] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [showWhatsappInput, setShowWhatsappInput] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);

    // Debounce para sliders (evita cálculos excesivos)
    useEffect(() => {
        if (price > 0 && term > 0) {
            setIsCalculating(true);
            const timer = setTimeout(() => {
                setIsCalculating(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [price, downPaymentPercent, term, interestRate]);

    // Scenario State
    const [scenarioDownPayment, setScenarioDownPayment] = useState(20);
    const [scenarioTerm, setScenarioTerm] = useState(20);
    const [scenarioRate, setScenarioRate] = useState(7.5);

    // Derived Values for Calculations (Safe fallback to 0)
    const priceNum = Number(price) || 0;
    const downPaymentPercentNum = Number(downPaymentPercent) || 0;
    const termNum = Number(term) || 0;
    const interestRateNum = Number(interestRate) || 0;
    const incomeNum = Number(income) || 0;

    const downPaymentAmount = (priceNum * downPaymentPercentNum) / 100;
    const loanAmount = priceNum - downPaymentAmount;
    const months = termNum * 12;
    const monthlyRate = interestRateNum / 100 / 12;

    // Calculations with defensive programming
    const calculatePayment = (principal, mRate, nMonths) => {
        // Validaciones defensivas
        if (!principal || principal <= 0) return 0;
        if (!nMonths || nMonths === 0) return 0;
        if (!mRate || mRate === 0) {
            // Si no hay tasa de interés, dividir el principal entre los meses
            return principal / nMonths;
        }

        // Cálculo estándar de cuota
        const numerator = principal * mRate * Math.pow(1 + mRate, nMonths);
        const denominator = Math.pow(1 + mRate, nMonths) - 1;

        if (denominator === 0) return 0;

        const result = numerator / denominator;
        return isFinite(result) ? result : 0;
    };

    const monthlyPrincipalInterest = calculatePayment(loanAmount, monthlyRate, months);

    // Insurance: (montoFinanciar * 0.015) / numeroMeses - con validación
    const monthlyInsurance = (months > 0 && loanAmount > 0) ? (loanAmount * 0.015) / months : 0;

    // Total Monthly Payment - validar que sean números finitos
    const totalMonthlyPayment = isFinite(monthlyPrincipalInterest) && isFinite(monthlyInsurance)
        ? monthlyPrincipalInterest + monthlyInsurance
        : 0;

    // CAT Calculations
    const appraisalFee = priceNum * 0.01; // 1%
    const openingFee = loanAmount * 0.02; // 2%
    const legalFee = priceNum * 0.015; // 1.5%
    const totalClosingCosts = appraisalFee + openingFee + legalFee;

    const totalInterest = (monthlyPrincipalInterest * months) - loanAmount;
    const totalPayable = totalMonthlyPayment * months;

    // CAT Formula con validación completa
    const calculateCAT = () => {
        if (!loanAmount || loanAmount <= 0) return 0;
        if (!termNum || termNum <= 0) return 0;

        const catValue = ((totalClosingCosts + totalInterest) / loanAmount) * 100 / termNum;
        return isFinite(catValue) ? catValue : 0;
    };

    const CAT = calculateCAT();

    // Validations
    const isDownPaymentLow = downPaymentPercentNum < 15;
    const isIncomeLow = incomeNum > 0 && totalMonthlyPayment > (incomeNum * 0.4);
    const isTermHigh = termNum > 30;

    // Amortization Schedule
    const amortizationSchedule = useMemo(() => {
        let balance = loanAmount;
        const schedule = [];
        for (let i = 1; i <= months; i++) {
            const interestPayment = balance * monthlyRate;
            const principalPayment = monthlyPrincipalInterest - interestPayment;
            balance -= principalPayment;
            if (balance < 0) balance = 0;

            schedule.push({
                month: i,
                payment: totalMonthlyPayment,
                principal: principalPayment,
                interest: interestPayment,
                insurance: monthlyInsurance,
                balance: balance
            });
        }
        return schedule;
    }, [loanAmount, monthlyRate, months, monthlyPrincipalInterest, totalMonthlyPayment, monthlyInsurance]);

    // Scenario Calculations
    const scenarioMonthlyPayment = calculatePayment(
        price - (price * scenarioDownPayment / 100),
        scenarioRate / 100 / 12,
        scenarioTerm * 12
    ) + ((price - (price * scenarioDownPayment / 100)) * 0.015) / (scenarioTerm * 12);


    // PDF Generation
    const generatePDF = () => {
        try {
            const doc = new jsPDF();

            // Header Branding
            const img = new Image();
            img.src = logoMain;
            try {
                // Size: ~120px is ~32mm width
                doc.addImage(img, 'PNG', 14, 10, 32, 12);
            } catch (e) {
                console.warn("Could not add logo", e);
            }

            doc.setFontSize(20);
            doc.setTextColor(26, 61, 92);
            doc.text("Análisis Hipotecario Profesional", 14, 35);

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 43);
            doc.text(`Institución: Servicios Futura | El Salvador`, 14, 48);

            const summaryData = [
                ["Precio Propiedad", formatPrice(price)],
                ["Enganche", `${formatPrice(downPaymentAmount)} (${downPaymentPercent}%)`],
                ["Monto Financiado", formatPrice(loanAmount)],
                ["Cuota Mensual Total", formatPrice(totalMonthlyPayment)],
                ["CAT Estimado", `${CAT.toFixed(2)}%`],
                ["Total a Pagar", formatPrice(totalPayable)]
            ];

            doc.autoTable({
                startY: 55,
                head: [['Concepto', 'Valor']],
                body: summaryData,
                theme: 'grid',
                headStyles: { fillColor: [26, 61, 92] }
            });

            // Add Footer
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text('Servicios Futura | El Salvador', 14, pageHeight - 15);
            doc.text('info@serviciosfutura.com', 14, pageHeight - 10);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text('© 2026 Servicios Futura', 14, pageHeight - 5);

            doc.save("analisis-hipotecario.pdf");
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Hubo un error al generar el PDF. Por favor intenta de nuevo.");
        }
    };

    const handleWhatsappShare = () => {
        const number = whatsappNumber || '50377777777'; // Default or user input
        const message = `Hola, he realizado un cálculo hipotecario en Servicios Futura:\n\nPropiedad: ${formatPrice(price)}\nEnganche: ${downPaymentPercent}%\nCuota Estimada: ${formatPrice(totalMonthlyPayment)}\n\nMe gustaría recibir más asesoría.`;
        window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, '_blank');
    };

    // Chart Data
    const pieData = {
        labels: ['Capital (Préstamo)', 'Intereses Totales'],
        datasets: [
            {
                data: [loanAmount, totalInterest],
                backgroundColor: ['#172B3B', '#EEA068'], // Secondary (Dark Blue), Primary (Orange)
                borderColor: ['#0f1c26', '#d98248'],
                borderWidth: 1,
                hoverOffset: 4
            },
        ],
    };

    const formatCompactNumber = (number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: 'compact',
            maximumFractionDigits: 1
        }).format(number);
    };

    const pieOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += formatCompactNumber(context.parsed);
                        }
                        return label;
                    }
                }
            }
        }
    };

    const lineData = {
        labels: amortizationSchedule.filter((_, i) => i % 12 === 0).map(r => `Año ${r.month / 12}`),
        datasets: [
            {
                label: 'Interés Pagado',
                data: amortizationSchedule.filter((_, i) => i % 12 === 0).map((r, idx) => {
                    const yearIdx = Math.floor(r.month / 12) - 1;
                    let sum = 0;
                    for (let i = 0; i < r.month; i++) sum += amortizationSchedule[i].interest;
                    return sum;
                }),
                borderColor: '#ef4444', // Red-500
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: '#ef4444',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBackgroundColor: '#ef4444',
                pointHoverBorderColor: '#fff',
                fill: true,
                tension: 0.4,
            },
            {
                label: 'Capital Pagado',
                data: amortizationSchedule.filter((_, i) => i % 12 === 0).map((r, idx) => {
                    return loanAmount - r.balance;
                }),
                borderColor: '#10b981', // Emerald-500
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: '#10b981',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBackgroundColor: '#10b981',
                pointHoverBorderColor: '#fff',
                fill: true,
                tension: 0.4,
            },
            {
                label: 'Saldo Pendiente',
                data: amortizationSchedule.filter((_, i) => i % 12 === 0).map(r => r.balance),
                borderColor: '#3b82f6', // Blue-500
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: '#3b82f6',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBackgroundColor: '#3b82f6',
                pointHoverBorderColor: '#fff',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    const lineOptions = {
        responsive: true,
        animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: { size: 14, weight: 'bold' },
                    color: '#475569'
                }
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#fff',
                bodyColor: '#e2e8f0',
                borderColor: '#3b82f6',
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${formatPrice(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    maxTicksLimit: 6,
                    font: { size: 10, weight: 'bold' },
                    color: '#64748b'
                }
            },
            y: {
                grid: {
                    color: 'rgba(148, 163, 184, 0.1)',
                    drawBorder: false
                },
                ticks: {
                    callback: (value) => formatCompactNumber(value),
                    font: { size: 10 },
                    color: '#64748b'
                }
            }
        },
        elements: {
            point: {
                radius: 0,
                hoverRadius: 6,
                hitRadius: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.6)'
            }
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Inputs */}
            <div className="lg:col-span-4 space-y-6">
                <Card className="border-0 shadow-2xl">
                    <CardHeader className="bg-slate-900 border-b-0 rounded-t-xl py-6">
                        <CardTitle className="text-white text-lg font-black uppercase tracking-tight italic">Parámetros del Crédito</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-6">
                        {/* Price */}
                        <div className="space-y-2">
                            <Label className="font-black text-[10px] uppercase text-slate-400">Precio de la Propiedad ($)</Label>
                            <NumericInput
                                value={price}
                                onChange={(val) => setPrice(val)}
                                min={5000} max={5000000}
                                placeholder="Ej. 150000"
                                prefix="$"
                                className="h-12 text-lg font-black"
                            />
                            <div className="px-2 pt-2">
                                <Slider
                                    value={[priceNum || 5000]}
                                    min={5000} max={1000000} step={5000}
                                    onValueChange={(v) => setPrice(v[0])}
                                />
                            </div>
                        </div>

                        {/* Down Payment */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-end">
                                <Label className="font-black text-[10px] uppercase text-slate-400">Enganche (%)</Label>
                                <span className="text-sm font-black text-blue-600">{downPaymentPercentNum}%</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={[downPaymentPercentNum || 20]}
                                    min={5} max={50} step={1}
                                    onValueChange={(v) => setDownPaymentPercent(v[0])}
                                />
                            </div>
                            <p className="text-[10px] font-bold text-slate-400 text-right uppercase tracking-widest">{formatPrice(downPaymentAmount)}</p>
                            {isDownPaymentLow && (
                                <p className="text-[10px] text-amber-600 font-black flex items-center bg-amber-50 p-2 rounded-lg border border-amber-100 uppercase tracking-tighter">
                                    <AlertTriangle size={12} className="mr-1" /> Se recomienda mínimo 15%
                                </p>
                            )}
                        </div>

                        {/* Term */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-end">
                                <Label className="font-black text-[10px] uppercase text-slate-400">Plazo (Años)</Label>
                                <span className="text-sm font-black text-slate-900">{termNum} años</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={[termNum || 20]}
                                    min={1} max={30} step={1}
                                    onValueChange={(v) => setTerm(v[0])}
                                />
                            </div>
                            {isTermHigh && <p className="text-[10px] text-red-500 font-black uppercase">Máximo permitido 30 años</p>}
                        </div>

                        {/* Rate */}
                        <div className="space-y-4 pt-2">
                            <div className="flex justify-between items-end">
                                <Label className="font-black text-[10px] uppercase text-slate-400">Tasa de Interés Anual (%)</Label>
                                <span className="text-sm font-black text-orange-600">{interestRateNum}%</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={[interestRateNum || 7.5]}
                                    min={0.1} max={25} step={0.1}
                                    onValueChange={(v) => setInterestRate(v[0])}
                                />
                            </div>
                        </div>

                        {/* Income */}
                        <div className="space-y-2">
                            <Label className="font-black text-[10px] uppercase text-slate-400">Ingresos Mensuales ($) <span className="text-[9px] text-slate-300 normal-case italic">(Para calificar)</span></Label>
                            <NumericInput
                                value={income}
                                onChange={(val) => setIncome(val)}
                                placeholder="Ej. 2500"
                                prefix="$"
                                className="h-11 font-bold"
                            />
                        </div>

                        {/* Legal Info */}
                        <div className="bg-slate-950 p-4 rounded-xl text-[10px] text-slate-400 border border-slate-800 shadow-inner">
                            <p className="font-black mb-2 flex items-center text-teal-400 uppercase tracking-widest"><Info className="w-3 h-3 mr-1" /> Normativa El Salvador (FSV/Bancos):</p>
                            <ul className="space-y-2 font-medium">
                                <li className="flex justify-between items-center"><span className="opacity-60">Enganche usual:</span> <strong className="text-white">10% - 20%</strong></li>
                                <li className="flex justify-between items-center"><span className="opacity-60">Plazo máximo:</span> <strong className="text-white">30 años</strong></li>
                                <li className="flex justify-between items-center"><span className="opacity-60">Relación Cuota:</span> <strong className="text-white">Máx. 40%</strong></li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                {/* Validations Alerts */}
                {isIncomeLow && (
                    <Alert variant="destructive" className="border-2 shadow-lg animate-bounce">
                        <AlertTriangle className="h-4 w-4" />
                        <div className="ml-2">
                            <AlertTitle className="font-black uppercase tracking-tight text-xs">Exceso de Capacidad</AlertTitle>
                            <AlertDescription className="text-[10px] font-bold">
                                La cuota excede el 40% de ingresos. Considera aumentar el enganche.
                            </AlertDescription>
                        </div>
                    </Alert>
                )}
            </div>

            {/* Middle Column: Results */}
            <div className="lg:col-span-5 space-y-6">
                {/* Main Result Card */}
                <Card className="bg-slate-900 border-0 shadow-2xl text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -mr-24 -mt-24"></div>
                    <CardContent className="p-8 text-center relative z-10">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Cuota Mensual Estimada</p>
                        {isCalculating ? (
                            <div className="py-8 mb-4 flex flex-col items-center justify-center">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mb-2"></div>
                                <p className="text-xs text-slate-400 font-bold uppercase">Calculando...</p>
                            </div>
                        ) : price > 0 && totalMonthlyPayment > 0 ? (
                            <h2 className="text-6xl font-black text-white mb-6 animate-in fade-in zoom-in duration-300 tracking-tighter italic">
                                {formatPrice(totalMonthlyPayment)}
                            </h2>
                        ) : (
                            <div className="py-2 mb-4">
                                <h2 className="text-2xl font-black text-slate-700 mb-2 tracking-tighter">--</h2>
                                <p className="text-xs text-slate-500 font-bold uppercase">Ingresa datos del crédito</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-6 text-sm mt-8 pt-8 border-t border-white/5">
                            <div className="text-left">
                                <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">Capital + Int.</p>
                                <p className="text-lg font-black text-slate-200">
                                    {price > 0 && totalMonthlyPayment > 0 ? formatPrice(monthlyPrincipalInterest) : '--'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">Seguro (Est.)</p>
                                <p className="text-lg font-black text-blue-400">
                                    {price > 0 && totalMonthlyPayment > 0 ? formatPrice(monthlyInsurance) : '--'}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <Card className="border-0 shadow-xl bg-white dark:bg-slate-900">
                        <CardContent className="p-5">
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">Total a Pagar</p>
                            <p className="text-xl font-black text-slate-950 dark:text-white">
                                {price > 0 && totalMonthlyPayment > 0 ? formatPrice(totalPayable) : '--'}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 shadow-xl bg-white dark:bg-slate-900">
                        <CardContent className="p-5">
                            <p className="text-[9px] font-black uppercase text-orange-500 tracking-widest mb-1">Total Intereses</p>
                            <p className="text-xl font-black text-orange-600">
                                {price > 0 && totalMonthlyPayment > 0 ? formatPrice(totalInterest) : '--'}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 shadow-xl bg-white dark:bg-slate-900">
                        <CardContent className="p-5">
                            <p className="text-[9px] font-black uppercase text-teal-500 tracking-widest mb-1">CAT (Costo Real)</p>
                            <p className="text-xl font-black text-teal-600">
                                {price > 0 && totalMonthlyPayment > 0 ? `${CAT.toFixed(2)}%` : '--'}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 shadow-xl bg-white dark:bg-slate-900">
                        <CardContent className="p-5">
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">Gastos Cierre</p>
                            <p className="text-xl font-black text-slate-950 dark:text-white">
                                {price > 0 && totalMonthlyPayment > 0 ? formatPrice(totalClosingCosts) : '--'}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Income Ratio Bar */}
                {income > 0 && (
                    <Card className="border-0 shadow-xl">
                        <CardContent className="p-5">
                            <div className="flex justify-between items-end mb-3">
                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Relación Deuda/Ingreso</span>
                                <span className={`text-sm font-black italic ${isIncomeLow ? "text-red-500" : "text-teal-600"}`}>
                                    {((totalMonthlyPayment / income) * 100).toFixed(1)}%
                                </span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3 p-0.5 border border-slate-200 dark:border-slate-700">
                                <div
                                    className={`h-full rounded-full transition-all duration-1000 ${isIncomeLow ? 'bg-gradient-to-r from-red-400 to-red-600 shadow-lg shadow-red-500/20' : 'bg-gradient-to-r from-teal-400 to-teal-600 shadow-lg shadow-teal-500/20'}`}
                                    style={{ width: `${Math.min(((totalMonthlyPayment / income) * 100), 100)}%` }}
                                ></div>
                            </div>
                            <div className="flex justify-between mt-2">
                                <p className="text-[8px] font-bold text-slate-400 uppercase italic">Zona Segura</p>
                                <p className="text-[8px] font-bold text-slate-400 uppercase italic">Límite: 40%</p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button onClick={generatePDF} className="h-14 rounded-2xl border-2 border-slate-950 bg-white text-slate-950 font-black uppercase tracking-widest text-xs hover:bg-slate-50 shadow-lg" variant="outline">
                        <Download className="mr-3 h-5 w-5" /> Exportar Análisis (PDF)
                    </Button>
                    <Button
                        className="h-14 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-green-500/20"
                        onClick={() => setShowWhatsappInput(!showWhatsappInput)}
                    >
                        <Share2 className="mr-3 h-5 w-5" /> Compartir en WhatsApp
                    </Button>
                </div>

                {showWhatsappInput && (
                    <div className="flex gap-2 animate-in slide-in-from-top-4 duration-300">
                        <Input
                            placeholder="Número (ej: 50377777777)"
                            value={whatsappNumber}
                            onChange={(e) => setWhatsappNumber(e.target.value)}
                            className="h-12 rounded-xl font-bold"
                        />
                        <Button onClick={handleWhatsappShare} size="icon" className="h-12 w-12 rounded-xl bg-slate-900">
                            <Phone className="h-5 w-5" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Right Column: Scenarios & Charts */}
            <div className="lg:col-span-3 space-y-6">
                <Card className="border-0 shadow-2xl bg-slate-50 dark:bg-slate-900/50">
                    <CardHeader>
                        <CardTitle className="text-sm font-black flex items-center gap-2 uppercase tracking-tighter italic">
                            <TrendingUp className="w-4 h-4 text-blue-500" /> Inteligencia Hipotecaria
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-10">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">Estructura del Pago Total</p>
                            <div className="h-48 flex justify-center">
                                <Pie data={pieData} options={pieOptions} />
                            </div>
                            <div className="mt-6 flex flex-col gap-2">
                                <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 rounded-lg text-[10px] font-black uppercase">
                                    <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-slate-900"></div> Capital</span>
                                    <span>{((loanAmount / totalPayable) * 100).toFixed(0)}%</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 rounded-lg text-[10px] font-black uppercase text-orange-600">
                                    <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-orange-500"></div> Intereses</span>
                                    <span>{((totalInterest / totalPayable) * 100).toFixed(0)}%</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-slate-200 dark:border-slate-800">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">Proyección Amortización Anual</p>
                            <div className="h-56">
                                <Line data={lineData} options={lineOptions} />
                            </div>
                            <p className="text-[8px] text-slate-400 mt-4 text-center font-bold italic leading-relaxed">
                                Gráfico comparativo: Cómo crece tu capital pagado frente al interés acumulado y el saldo remanente.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Full Width: Amortization Table */}
            <div className="lg:col-span-12">
                <Card className="border-0 shadow-2xl overflow-hidden">
                    <CardHeader className="bg-slate-900 py-6">
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-white text-lg font-black uppercase tracking-tight italic">Cronograma de Pagos Detallado</CardTitle>
                            <Badge className="bg-blue-500 text-white font-black uppercase text-[10px]">Cuota Fija Nivelada</Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50 dark:bg-slate-900 hover:bg-slate-50 border-white/0">
                                        <TableHead className="font-black text-[11px] uppercase text-slate-400 h-14 pl-6">N° Mes</TableHead>
                                        <TableHead className="font-black text-[11px] uppercase text-slate-400">Cuota Total</TableHead>
                                        <TableHead className="font-black text-[11px] uppercase text-slate-900 dark:text-white">A Capital</TableHead>
                                        <TableHead className="font-black text-[11px] uppercase text-orange-500">Interés</TableHead>
                                        <TableHead className="font-black text-[11px] uppercase text-blue-500">Seguros</TableHead>
                                        <TableHead className="font-black text-[11px] uppercase text-slate-400 text-right pr-6">Saldo Restante</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {amortizationSchedule.slice(0, showAllAmortization ? undefined : 12).map((row, i) => (
                                        <TableRow key={row.month} className={cn(
                                            "hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors border-slate-50 dark:border-slate-800/50",
                                            i % 2 === 0 ? "bg-white dark:bg-slate-950" : "bg-slate-50/40 dark:bg-slate-900/20"
                                        )}>
                                            <TableCell className="font-black text-xs pl-6">{row.month}</TableCell>
                                            <TableCell className="font-bold text-xs">{formatPrice(row.payment)}</TableCell>
                                            <TableCell className="font-black text-xs text-slate-900 dark:text-white">{formatPrice(row.principal)}</TableCell>
                                            <TableCell className="font-medium text-xs text-orange-600">{formatPrice(row.interest)}</TableCell>
                                            <TableCell className="font-medium text-xs text-blue-500">{formatPrice(row.insurance)}</TableCell>
                                            <TableCell className="font-black text-xs text-right pr-6">{formatPrice(row.balance)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {!showAllAmortization && (
                            <div className="p-6 bg-slate-50/80 dark:bg-slate-900/50 text-center border-t border-slate-100 dark:border-slate-800">
                                <Button variant="ghost" className="font-black uppercase text-[10px] text-blue-600 tracking-widest hover:bg-blue-50" onClick={() => setShowAllAmortization(true)}>
                                    Expandir todos los {months} meses de amortización
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default MortgageCalculator;
